export interface OfflineFullSyncDependencies {
  getEffectiveNetworkOnline: () => Promise<boolean>;
  getOnlineStatus: () => boolean;
  setOnlineStatus: (isOnline: boolean) => void;
  saveAllDrafts: () => Promise<void>;
  syncPendingReviews: () => Promise<void>;
  syncOfflineRooms: () => Promise<void>;
  syncOfflineOnDemandItems: () => Promise<void>;
  syncPendingResponses: () => Promise<void>;
  processPhotoQueue?: () => Promise<void>;
  syncPendingPhotoNotes?: () => Promise<void>;
  syncReviewLifecycleActions?: () => Promise<void>;
  recomputeReviewSyncStatuses: () => Promise<void>;
  logger?: Pick<Console, 'error'>;
}

export class OfflineFullSyncService {
  private syncPromise: Promise<void> | null = null;
  private startedRunCount = 0;
  private overlappedRunCount = 0;

  constructor(private readonly dependencies: OfflineFullSyncDependencies) {}

  isSyncing(): boolean {
    return this.syncPromise !== null;
  }

  getDebugState() {
    return {
      isSyncing: this.isSyncing(),
      startedRunCount: this.startedRunCount,
      overlappedRunCount: this.overlappedRunCount,
    };
  }

  async performFullSync(): Promise<void> {
    if (this.syncPromise) {
      this.overlappedRunCount += 1;
      return this.syncPromise;
    }

    this.syncPromise = this.performFullSyncLocked();

    try {
      await this.syncPromise;
    } finally {
      this.syncPromise = null;
    }
  }

  private async performFullSyncLocked(): Promise<void> {
    try {
      this.dependencies.setOnlineStatus(await this.dependencies.getEffectiveNetworkOnline());
    } catch {
      // Keep the last known network value if the explicit check fails.
    }

    if (!this.dependencies.getOnlineStatus()) {
      return;
    }

    this.startedRunCount += 1;
    await this.doPerformFullSync();
  }

  private async doPerformFullSync(): Promise<void> {
    try {
      // Flush dirty draft data before processing pending responses so the server
      // receives the latest local edit for each item.
      await this.dependencies.saveAllDrafts();

      // Reviews create the property/review records required by later sync steps.
      await this.dependencies.syncPendingReviews();
      await this.dependencies.syncOfflineRooms();
      await this.dependencies.syncOfflineOnDemandItems();
      await this.dependencies.syncPendingResponses();
      await this.dependencies.processPhotoQueue?.();
      await this.dependencies.syncPendingPhotoNotes?.();
      await this.dependencies.syncReviewLifecycleActions?.();
      await this.dependencies.recomputeReviewSyncStatuses();
    } catch (error) {
      this.dependencies.logger?.error('[OfflineReviewService] Full sync failed:', error);
      throw error;
    }
  }
}
